import {
    Calculation,
    PaymentFrequency,
    PaymentStructure,
} from '@servicetitan/financing-api-client';
import { PlanCard } from './PlanCard';
import { Flex, Grid, Text } from '@servicetitan/anvil2';
import {
    plansListGrid,
    legalDisclosure,
    plansContainer,
    emptyPlansList,
    emptyPlansListText,
    selectorSection,
} from './PlansList.module.scss';
import {
    SetupDataStore,
    useProviderConfig,
    SecondLookApplicationSubmittedStore,
} from '@servicetitan/financing-workflow-shared';
import { useLegalDisclosure } from '@servicetitan/financing-utils-shared';
import { ReactNode, useState, useMemo, useRef, useEffect } from 'react';
import { PaymentFrequencyTabs } from './PaymentFrequencyTabs';
import { MidTabs, MidInfo } from './MidTabs';
import { v4 as uuidv4 } from 'uuid';

const SELECTOR_TITLE_DEFAULT = 'Select a Plan';

// Filter plans by frequency - moved outside component for performance
const filterPlansByFrequency = (frequency: PaymentFrequency, plansToFilter: Calculation[]) => {
    return plansToFilter.filter(plan => {
        // If no paymentFrequency field exists, show all plans for the 'weekly' frequency to maintain compatibility
        if (!plan.paymentFrequency) {
            return frequency === PaymentFrequency.Weekly;
        }

        // Match frequency values case-insensitively to handle different casing from API
        return plan.paymentFrequency.toLowerCase() === frequency.toLowerCase();
    });
};

export const deduplicatePlans = (plans: Calculation[]): Calculation[] => {
    const seen = new Set<string>();
    return plans.filter(plan => {
        if (!plan.planNumber || seen.has(plan.planNumber)) {
            return false;
        }
        seen.add(plan.planNumber);
        return true;
    });
};

export interface PlansListProps {
    plans: Calculation[];
    instructions?: string | ReactNode;
    onPlanSelected?: (plan: Calculation) => void;
    disableSelection?: boolean;
    /**
     * Selector heading; defaults to "Select a Plan". The orchestrator passes an override when a
     * pre-approval card sits above the selector.
     */
    title?: string;
    /**
     * When false, the selector (tabs, title, grid, empty state) is suppressed while the rest of the
     * region — leading content and footer disclosures — still renders. Used when a pre-approval card
     * is shown with no other plans available.
     */
    showSelector?: boolean;
    /**
     * Rendered inside `.plans-container` above the selector (e.g. the pre-approval section), so it
     * shares the container-query context the selector grid depends on.
     */
    leadingContent?: ReactNode;
    /** Rendered above the footer legal disclosures (e.g. the GreenSky pre-approval notice). */
    leadingDisclosure?: ReactNode;
}

/**
 * Presentational plan selector plus the page's footer legal disclosures. Owns plan shaping (merchant
 * and payment-frequency tabs, deduplication) and the responsive `.plans-container` layout, but is
 * agnostic of pre-approvals and page-level loading/empty states — the orchestrating container drives
 * those by composing the `title`, `showSelector`, `leadingContent`, and `leadingDisclosure` slots.
 */
export const PlansList = ({
    plans,
    instructions,
    onPlanSelected,
    disableSelection = false,
    title = SELECTOR_TITLE_DEFAULT,
    showSelector = true,
    leadingContent,
    leadingDisclosure,
}: PlansListProps) => {
    const { setupData } = SetupDataStore.useStore();
    const {
        legalDisclosureCategory,
        customLegalDisclosureTextForDeferredInterest,
        enablePaymentFrequencyTabs,
        supportsMultipleRateSheets,
    } = useProviderConfig();
    const { isSecondLookApplicationSubmitted } = SecondLookApplicationSubmittedStore.useStore();
    const legalDisclosureResult = useLegalDisclosure({
        providerName: setupData?.provider,
        tenantId: setupData?.tenantId,
        category: legalDisclosureCategory,
    });

    const isTechnician = setupData?.role === 'technician';

    const distinctMids = useMemo<MidInfo[]>(() => {
        const seen = new Set<string>();
        const result: MidInfo[] = [];
        for (const plan of plans) {
            if (plan.merchantId && !seen.has(plan.merchantId)) {
                seen.add(plan.merchantId);
                result.push({
                    merchantId: plan.merchantId,
                    // TODO: Remove any cast when FinPlansApi is updated
                    midName: (plan as any).merchantName ?? null,
                });
            }
        }
        return result;
    }, [plans]);

    const showMidTabs =
        isTechnician &&
        supportsMultipleRateSheets === true &&
        !enablePaymentFrequencyTabs &&
        distinctMids.length > 1;

    const [midSelection, setMidSelection] = useState<string | null>(null);

    useEffect(() => {
        if (!showMidTabs) {
            setMidSelection(null);
        }
    }, [showMidTabs]);

    const validatedMidSelection =
        showMidTabs && distinctMids.length > 0
            ? midSelection != null && distinctMids.some(m => m.merchantId === midSelection)
                ? midSelection
                : distinctMids[0].merchantId
            : null;

    const midFilteredPlans = useMemo(() => {
        if (!showMidTabs || validatedMidSelection === null) {
            return plans;
        }
        return plans.filter(p => p.merchantId === validatedMidSelection);
    }, [plans, showMidTabs, validatedMidSelection]);

    const [currentFrequency, setCurrentFrequency] = useState<PaymentFrequency>(
        PaymentFrequency.Weekly,
    );

    // Ref for the grid container to control scroll position
    const gridRef = useRef<HTMLDivElement>(null);

    // Handle frequency change from payment frequency tabs
    const handleFrequencyChange = (frequency: PaymentFrequency) => {
        setCurrentFrequency(frequency);
    };

    const uniquePlans = useMemo(() => deduplicatePlans(midFilteredPlans), [midFilteredPlans]);

    // Derive displayed plans using useMemo - no separate state needed
    const plansToShow = useMemo(() => {
        if (!enablePaymentFrequencyTabs) {
            return uniquePlans;
        }
        return filterPlansByFrequency(currentFrequency, uniquePlans);
    }, [uniquePlans, currentFrequency, enablePaymentFrequencyTabs]);

    // Generate stable UUID keys for each plan object - only when plans change, not on tab switch
    const planKeys = useMemo(() => {
        const keyMap = new Map<Calculation, string>();
        plans.forEach(plan => {
            keyMap.set(plan, uuidv4());
        });
        return keyMap;
    }, [plans]);

    // Check if any plan has deferred interest
    const hasDeferredInterestPlan = useMemo(() => {
        return plansToShow.some(plan => {
            // `Calculation` (fin-plans-api) and `PaymentStructure` (financing-api) are two
            // identically-valued generated enums; reconcile them so the comparison is enum-type-safe.
            const paymentStructure = plan.paymentStructure as PaymentStructure;
            return (
                paymentStructure === PaymentStructure.DeferredInterest ||
                paymentStructure === PaymentStructure.DeferredInterestAndPrincipal
            );
        });
    }, [plansToShow]);

    // Show custom disclosure for deferred interest plans only when:
    // 1. The config has a deferred interest disclosure text
    // 2. There are deferred interest plans in the list
    // 3. The second look application has been submitted (meaning we're showing Turns plans)
    const shouldShowDeferredInterestDisclosure =
        customLegalDisclosureTextForDeferredInterest != null &&
        hasDeferredInterestPlan &&
        isSecondLookApplicationSubmitted;

    // For the array (GreenSky) branch, split by content: the entry containing an <svg> is the icon
    // (greensky-adapter hardcodes an inline <svg viewBox="0 0 36 41"> for the Equal-Opportunity-Lender
    // mark — see GetLegalDisclosuresQueryHandler), and the remaining entries are the copy.
    // Degrades gracefully when no <svg> entry is found (e.g. a CMS-raw shape with an <img>): the
    // copy renders as a single column with no icon, avoiding the float/wrap bug without error.
    const iconHtml = Array.isArray(legalDisclosureResult)
        ? (legalDisclosureResult.find(html => /<svg/i.test(html)) ?? '')
        : '';
    const copyHtml = Array.isArray(legalDisclosureResult)
        ? legalDisclosureResult.filter(html => !/<svg/i.test(html)).join('<br />')
        : '';

    // For the string branch only
    const formattedLegalDisclosureText = Array.isArray(legalDisclosureResult)
        ? ''
        : legalDisclosureResult;

    const showFilteredEmptyState = showMidTabs || enablePaymentFrequencyTabs;

    return (
        <Flex direction="column" gap="4" className={plansContainer}>
            {leadingContent}

            {showSelector && (
                // Extra top margin (via selectorSection) only when leading content sits above,
                // separating the two sections. The inner gap mirrors the page column's gap="4".
                <Flex
                    direction="column"
                    gap="4"
                    className={leadingContent ? selectorSection : undefined}
                >
                    {showMidTabs && validatedMidSelection !== null ? (
                        <MidTabs
                            mids={distinctMids}
                            selectedMerchantId={validatedMidSelection}
                            onMidChange={setMidSelection}
                            scrollContainerRef={gridRef}
                        />
                    ) : null}
                    {enablePaymentFrequencyTabs ? (
                        <PaymentFrequencyTabs
                            plans={plans}
                            onFrequencyChange={handleFrequencyChange}
                            scrollContainerRef={gridRef}
                        />
                    ) : null}

                    <Text variant="headline" el="h2">
                        {title}
                    </Text>
                    {instructions && (
                        <Text variant="body" subdued size="small" style={{ marginBottom: '16px' }}>
                            {instructions}
                        </Text>
                    )}

                    {plansToShow.length > 0 ? (
                        <Grid ref={gridRef} className={plansListGrid} role="list">
                            {plansToShow.map(plan => (
                                <PlanCard
                                    key={planKeys.get(plan)}
                                    plan={plan}
                                    onPlanSelected={onPlanSelected}
                                    showLowestPayment={plansToShow.length > 1}
                                    currentFrequency={
                                        enablePaymentFrequencyTabs ? currentFrequency : undefined
                                    }
                                    disableSelection={disableSelection}
                                />
                            ))}
                        </Grid>
                    ) : showFilteredEmptyState ? (
                        <Flex
                            justifyContent="center"
                            alignItems="center"
                            className={emptyPlansList}
                        >
                            <Text variant="body" className={emptyPlansListText}>
                                No plans available to display.
                            </Text>
                        </Flex>
                    ) : null}
                </Flex>
            )}

            {leadingDisclosure}

            {/* Custom legal disclosure text for deferred interest plans */}
            {shouldShowDeferredInterestDisclosure && (
                <Text
                    variant="body"
                    subdued
                    size="small"
                    dangerouslySetInnerHTML={{
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        __html: customLegalDisclosureTextForDeferredInterest,
                    }}
                />
            )}

            {/* Standard legal disclosure — array branch: two-column icon + copy (GreenSky) */}
            {Array.isArray(legalDisclosureResult)
                ? (iconHtml || copyHtml) && (
                      <Flex
                          direction="row"
                          gap="3"
                          alignItems="flex-start"
                          className={legalDisclosure}
                      >
                          {iconHtml && (
                              <span
                                  dangerouslySetInnerHTML={{
                                      // eslint-disable-next-line @typescript-eslint/naming-convention
                                      __html: iconHtml,
                                  }}
                              />
                          )}
                          {copyHtml && (
                              <Text
                                  variant="body"
                                  subdued
                                  size="small"
                                  flexGrow={1}
                                  dangerouslySetInnerHTML={{
                                      // eslint-disable-next-line @typescript-eslint/naming-convention
                                      __html: copyHtml,
                                  }}
                              />
                          )}
                      </Flex>
                  )
                : formattedLegalDisclosureText !== '' && (
                      <Text
                          variant="body"
                          subdued
                          size="small"
                          className={legalDisclosure}
                          dangerouslySetInnerHTML={{
                              // eslint-disable-next-line @typescript-eslint/naming-convention
                              __html: formattedLegalDisclosureText,
                          }}
                      />
                  )}
        </Flex>
    );
};
